/**
 * GasCompositionAnalysis Component
 * Industry-grade gas composition analysis with drill-down capabilities
 * Shows detailed breakdown of gas components and their impact on compressor performance
 */

import { useState, useMemo } from 'react'
import Plot from 'react-plotly.js'
import {
  Droplets,
  Thermometer,
  Gauge,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Info,
  ChevronDown,
  ChevronRight,
  Beaker,
  Zap,
  Wind,
  Activity,
  Target,
  BarChart3,
  PieChart,
  Layers
} from 'lucide-react'
import clsx from 'clsx'

// Gas component interface
interface GasComponent {
  name: string
  formula: string
  value: number
  unit: string
  normalRange: { min: number; max: number }
  trend: 'up' | 'down' | 'stable'
  trendValue: number
  category: 'hydrocarbon' | 'inert' | 'contaminant' | 'other'
  impact: 'positive' | 'negative' | 'neutral'
  description: string
}

// Performance impact interface
interface PerformanceImpact {
  metric: string
  currentValue: number
  optimalValue: number
  unit: string
  deviation: number
  severity: 'good' | 'warning' | 'critical'
  recommendation?: string
}

// Historical data point
interface HistoricalPoint {
  timestamp: string
  molecularWeight: number
  heatingValue: number
  compressionRatio: number
}

interface GasCompositionAnalysisProps {
  assetId: string
  gasComponents: GasComponent[]
  molecularWeight: number
  targetMolecularWeight: number
  heatingValue: number
  compressionRatio: number
  performanceImpacts: PerformanceImpact[]
  historicalData?: HistoricalPoint[]
  aiAnalysis?: string
  lastUpdated: string
  className?: string
}

export default function GasCompositionAnalysis({
  assetId,
  gasComponents,
  molecularWeight: rawMolecularWeight,
  targetMolecularWeight: rawTargetMolecularWeight,
  heatingValue: rawHeatingValue,
  compressionRatio: rawCompressionRatio,
  performanceImpacts,
  historicalData = [],
  aiAnalysis,
  lastUpdated,
  className
}: GasCompositionAnalysisProps) {
  const [activeTab, setActiveTab] = useState<'composition' | 'impact' | 'trends' | 'analysis'>('composition')
  const [expandedComponent, setExpandedComponent] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  // Guard against undefined numeric values
  const molecularWeight = rawMolecularWeight ?? 18.0
  const targetMolecularWeight = rawTargetMolecularWeight ?? 18.0
  const heatingValue = rawHeatingValue ?? 1000
  const compressionRatio = rawCompressionRatio ?? 2.5

  // Guard against undefined gasComponents - use throughout component
  const safeGasComponents = useMemo(() =>
    Array.isArray(gasComponents) ? gasComponents : [],
    [gasComponents]
  )

  // Guard against undefined performanceImpacts
  const safePerformanceImpacts = useMemo(() =>
    Array.isArray(performanceImpacts) ? performanceImpacts : [],
    [performanceImpacts]
  )

  // Calculate totals and aggregates
  const compositionStats = useMemo(() => {
    const hydrocarbons = safeGasComponents.filter(g => g.category === 'hydrocarbon')
    const inerts = safeGasComponents.filter(g => g.category === 'inert')
    const contaminants = safeGasComponents.filter(g => g.category === 'contaminant')

    const hydrocarbonTotal = hydrocarbons.reduce((sum, g) => sum + g.value, 0)
    const inertTotal = inerts.reduce((sum, g) => sum + g.value, 0)
    const contaminantTotal = contaminants.reduce((sum, g) => sum + g.value, 0)

    const outOfRange = safeGasComponents.filter(
      g => g.value < g.normalRange.min || g.value > g.normalRange.max
    )

    return {
      hydrocarbonTotal,
      inertTotal,
      contaminantTotal,
      outOfRangeCount: outOfRange.length,
      totalComponents: safeGasComponents.length,
      healthScore: Math.max(0, 100 - (outOfRange.length * 15))
    }
  }, [safeGasComponents])

  // Molecular weight deviation
  const mwDeviation = ((molecularWeight - targetMolecularWeight) / targetMolecularWeight) * 100

  // Plotly configuration
  const plotConfig = { displayModeBar: false, responsive: true }
  const plotLayout = {
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#94a3b8', size: 11 },
    margin: { t: 30, r: 20, b: 40, l: 50 }
  }

  // Composition pie chart data
  const pieChartData = useMemo(() => {
    const categories = [
      { name: 'Hydrocarbons', value: compositionStats.hydrocarbonTotal, color: '#3b82f6' },
      { name: 'Inerts', value: compositionStats.inertTotal, color: '#8b5cf6' },
      { name: 'Contaminants', value: compositionStats.contaminantTotal, color: '#ef4444' }
    ].filter(c => c.value > 0)

    return [{
      type: 'pie' as const,
      values: categories.map(c => c.value),
      labels: categories.map(c => c.name),
      marker: { colors: categories.map(c => c.color) },
      hole: 0.5,
      textinfo: 'label+percent' as const,
      textposition: 'outside' as const,
      hovertemplate: '%{label}: %{value:.2f}%<extra></extra>'
    }]
  }, [compositionStats])

  // Note: Bar chart data generation available if needed for future visualization
  // Using selectedCategory for filtering in component rendering below

  // Historical trend chart
  const trendChartData = useMemo(() => {
    if (historicalData.length === 0) return []

    return [
      {
        type: 'scatter' as const,
        mode: 'lines+markers' as const,
        name: 'Molecular Weight',
        x: historicalData.map(h => h.timestamp),
        y: historicalData.map(h => h.molecularWeight),
        line: { color: '#3b82f6', width: 2 },
        marker: { size: 6 },
        yaxis: 'y'
      },
      {
        type: 'scatter' as const,
        mode: 'lines+markers' as const,
        name: 'Heating Value',
        x: historicalData.map(h => h.timestamp),
        y: historicalData.map(h => h.heatingValue),
        line: { color: '#22c55e', width: 2 },
        marker: { size: 6 },
        yaxis: 'y2'
      }
    ]
  }, [historicalData])

  // Component status indicator
  const getComponentStatus = (component: GasComponent) => {
    if (component.value < component.normalRange.min) return 'low'
    if (component.value > component.normalRange.max) return 'high'
    return 'normal'
  }

  // Render component detail card
  const renderComponentDetail = (component: GasComponent) => {
    const status = getComponentStatus(component)
    const isExpanded = expandedComponent === component.formula

    return (
      <div
        key={component.formula}
        className={clsx(
          'bg-surface-800/50 rounded-lg border transition-all duration-200',
          status === 'normal' ? 'border-surface-700' :
          status === 'high' ? 'border-status-warning/50' : 'border-status-critical/50',
          isExpanded && 'ring-1 ring-primary-500/30'
        )}
      >
        <button
          onClick={() => setExpandedComponent(isExpanded ? null : component.formula)}
          className="w-full p-3 flex items-center justify-between hover:bg-surface-700/30 transition-colors rounded-lg"
        >
          <div className="flex items-center gap-3">
            <div className={clsx(
              'w-10 h-10 rounded-lg flex items-center justify-center text-sm font-mono font-bold',
              component.category === 'hydrocarbon' ? 'bg-blue-500/20 text-blue-400' :
              component.category === 'inert' ? 'bg-purple-500/20 text-purple-400' :
              'bg-red-500/20 text-red-400'
            )}>
              {component.formula}
            </div>
            <div className="text-left">
              <div className="text-sm font-medium text-surface-100">{component.name}</div>
              <div className="text-xs text-surface-400">{component.category}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className={clsx(
                'text-lg font-semibold',
                status === 'normal' ? 'text-status-healthy' :
                status === 'high' ? 'text-status-warning' : 'text-status-critical'
              )}>
                {component.value.toFixed(3)}%
              </div>
              <div className="flex items-center gap-1 text-xs">
                {component.trend === 'up' ? (
                  <TrendingUp size={12} className="text-status-warning" />
                ) : component.trend === 'down' ? (
                  <TrendingDown size={12} className="text-status-healthy" />
                ) : (
                  <Activity size={12} className="text-surface-400" />
                )}
                <span className={clsx(
                  component.trend === 'up' ? 'text-status-warning' :
                  component.trend === 'down' ? 'text-status-healthy' : 'text-surface-400'
                )}>
                  {component.trendValue > 0 ? '+' : ''}{component.trendValue.toFixed(2)}%
                </span>
              </div>
            </div>
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </div>
        </button>

        {isExpanded && (
          <div className="px-3 pb-3 border-t border-surface-700/50 mt-2 pt-3">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <div className="text-xs text-surface-400 mb-1">Normal Range</div>
                <div className="text-sm text-surface-200">
                  {component.normalRange.min.toFixed(3)} - {component.normalRange.max.toFixed(3)}%
                </div>
              </div>
              <div>
                <div className="text-xs text-surface-400 mb-1">Performance Impact</div>
                <div className={clsx(
                  'text-sm font-medium',
                  component.impact === 'positive' ? 'text-status-healthy' :
                  component.impact === 'negative' ? 'text-status-warning' : 'text-surface-300'
                )}>
                  {component.impact.charAt(0).toUpperCase() + component.impact.slice(1)}
                </div>
              </div>
            </div>
            <div className="text-xs text-surface-400 bg-surface-900/50 p-2 rounded">
              {component.description}
            </div>
            {/* Mini gauge showing position in range */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-surface-500 mb-1">
                <span>{component.normalRange.min.toFixed(2)}</span>
                <span>Current: {component.value.toFixed(3)}</span>
                <span>{component.normalRange.max.toFixed(2)}</span>
              </div>
              <div className="h-2 bg-surface-700 rounded-full relative overflow-hidden">
                <div
                  className="absolute inset-y-0 bg-status-healthy/30"
                  style={{
                    left: '10%',
                    right: '10%'
                  }}
                />
                <div
                  className={clsx(
                    'absolute w-3 h-3 rounded-full -top-0.5 transform -translate-x-1/2',
                    status === 'normal' ? 'bg-status-healthy' :
                    status === 'high' ? 'bg-status-warning' : 'bg-status-critical'
                  )}
                  style={{
                    left: `${Math.min(100, Math.max(0, ((component.value - component.normalRange.min) / (component.normalRange.max - component.normalRange.min)) * 80 + 10))}%`
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={clsx('bg-surface-800 rounded-xl border border-surface-700 overflow-hidden', className)}>
      {/* Header */}
      <div className="p-4 border-b border-surface-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Beaker size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-surface-100">Gas Composition Analysis</h3>
              <p className="text-sm text-surface-400">Asset: {assetId} | Updated: {lastUpdated}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Composition Health Score */}
            <div className="text-center px-4 py-2 bg-surface-900/50 rounded-lg">
              <div className={clsx(
                'text-2xl font-bold',
                compositionStats.healthScore >= 85 ? 'text-status-healthy' :
                compositionStats.healthScore >= 70 ? 'text-status-warning' : 'text-status-critical'
              )}>
                {compositionStats.healthScore}%
              </div>
              <div className="text-xs text-surface-400">Health Score</div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mt-4">
          {[
            { id: 'composition', label: 'Composition', icon: PieChart },
            { id: 'impact', label: 'Performance Impact', icon: Zap },
            { id: 'trends', label: 'Historical Trends', icon: TrendingUp },
            { id: 'analysis', label: 'AI Analysis', icon: Activity }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700/50'
              )}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics Bar */}
      <div className="grid grid-cols-4 gap-4 p-4 bg-surface-900/30 border-b border-surface-700">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Gauge size={14} className="text-blue-400" />
            <span className="text-xs text-surface-400">Molecular Weight</span>
          </div>
          <div className="text-xl font-bold text-surface-100">{molecularWeight.toFixed(2)}</div>
          <div className={clsx(
            'text-xs',
            Math.abs(mwDeviation) <= 5 ? 'text-status-healthy' :
            Math.abs(mwDeviation) <= 10 ? 'text-status-warning' : 'text-status-critical'
          )}>
            {mwDeviation > 0 ? '+' : ''}{mwDeviation.toFixed(1)}% from target
          </div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Thermometer size={14} className="text-orange-400" />
            <span className="text-xs text-surface-400">Heating Value</span>
          </div>
          <div className="text-xl font-bold text-surface-100">{heatingValue.toFixed(0)}</div>
          <div className="text-xs text-surface-400">BTU/scf</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Wind size={14} className="text-cyan-400" />
            <span className="text-xs text-surface-400">Compression Ratio</span>
          </div>
          <div className="text-xl font-bold text-surface-100">{compressionRatio.toFixed(2)}</div>
          <div className="text-xs text-surface-400">:1</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <AlertTriangle size={14} className="text-yellow-400" />
            <span className="text-xs text-surface-400">Out of Range</span>
          </div>
          <div className={clsx(
            'text-xl font-bold',
            compositionStats.outOfRangeCount === 0 ? 'text-status-healthy' :
            compositionStats.outOfRangeCount <= 2 ? 'text-status-warning' : 'text-status-critical'
          )}>
            {compositionStats.outOfRangeCount}/{compositionStats.totalComponents}
          </div>
          <div className="text-xs text-surface-400">components</div>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4">
        {/* Composition Tab */}
        {activeTab === 'composition' && (
          <div className="grid grid-cols-12 gap-4">
            {/* Category Distribution */}
            <div className="col-span-4">
              <div className="bg-surface-900/50 rounded-lg p-4 h-full">
                <h4 className="text-sm font-medium text-surface-300 mb-3 flex items-center gap-2">
                  <Layers size={14} />
                  Category Distribution
                </h4>
                <Plot
                  data={pieChartData}
                  layout={{
                    ...plotLayout,
                    height: 220,
                    showlegend: false,
                    annotations: [{
                      text: `${compositionStats.totalComponents}<br>Components`,
                      showarrow: false,
                      font: { size: 14, color: '#e2e8f0' }
                    }]
                  }}
                  config={plotConfig}
                  className="w-full"
                />
                {/* Category Legend */}
                <div className="flex flex-col gap-2 mt-2">
                  {[
                    { name: 'Hydrocarbons', value: compositionStats.hydrocarbonTotal, color: 'bg-blue-500' },
                    { name: 'Inerts', value: compositionStats.inertTotal, color: 'bg-purple-500' },
                    { name: 'Contaminants', value: compositionStats.contaminantTotal, color: 'bg-red-500' }
                  ].map(cat => (
                    <button
                      key={cat.name}
                      onClick={() => setSelectedCategory(
                        selectedCategory === cat.name.toLowerCase().slice(0, -1) ? null : cat.name.toLowerCase().slice(0, -1)
                      )}
                      className={clsx(
                        'flex items-center justify-between p-2 rounded-lg transition-colors',
                        selectedCategory === cat.name.toLowerCase().slice(0, -1)
                          ? 'bg-surface-700'
                          : 'hover:bg-surface-800'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <div className={clsx('w-3 h-3 rounded-full', cat.color)} />
                        <span className="text-sm text-surface-300">{cat.name}</span>
                      </div>
                      <span className="text-sm font-medium text-surface-100">{cat.value.toFixed(2)}%</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Component Details */}
            <div className="col-span-8">
              <div className="bg-surface-900/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-surface-300 flex items-center gap-2">
                    <BarChart3 size={14} />
                    Component Breakdown
                    {selectedCategory && (
                      <span className="text-xs text-primary-400 ml-2">
                        Filtered: {selectedCategory}
                      </span>
                    )}
                  </h4>
                  {selectedCategory && (
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className="text-xs text-surface-400 hover:text-surface-200"
                    >
                      Clear filter
                    </button>
                  )}
                </div>
                <div className="grid gap-2 max-h-[400px] overflow-y-auto pr-2">
                  {(selectedCategory
                    ? safeGasComponents.filter(g => g.category === selectedCategory)
                    : safeGasComponents
                  ).map(renderComponentDetail)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Impact Tab */}
        {activeTab === 'impact' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {safePerformanceImpacts.map((impact, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    'bg-surface-900/50 rounded-lg p-4 border',
                    impact.severity === 'good' ? 'border-status-healthy/30' :
                    impact.severity === 'warning' ? 'border-status-warning/30' : 'border-status-critical/30'
                  )}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-medium text-surface-200">{impact.metric}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        {impact.severity === 'good' ? (
                          <CheckCircle size={14} className="text-status-healthy" />
                        ) : impact.severity === 'warning' ? (
                          <AlertTriangle size={14} className="text-status-warning" />
                        ) : (
                          <AlertTriangle size={14} className="text-status-critical" />
                        )}
                        <span className={clsx(
                          'text-xs',
                          impact.severity === 'good' ? 'text-status-healthy' :
                          impact.severity === 'warning' ? 'text-status-warning' : 'text-status-critical'
                        )}>
                          {impact.severity === 'good' ? 'Within optimal range' :
                           impact.severity === 'warning' ? 'Approaching threshold' : 'Requires attention'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-surface-100">
                        {impact.currentValue.toFixed(1)}
                      </div>
                      <div className="text-xs text-surface-400">{impact.unit}</div>
                    </div>
                  </div>

                  {/* Deviation gauge */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-surface-500 mb-1">
                      <span>Optimal: {impact.optimalValue}{impact.unit}</span>
                      <span className={clsx(
                        impact.deviation > 0 ? 'text-status-warning' : 'text-status-healthy'
                      )}>
                        {impact.deviation > 0 ? '+' : ''}{impact.deviation.toFixed(1)}% deviation
                      </span>
                    </div>
                    <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                      <div
                        className={clsx(
                          'h-full rounded-full transition-all',
                          impact.severity === 'good' ? 'bg-status-healthy' :
                          impact.severity === 'warning' ? 'bg-status-warning' : 'bg-status-critical'
                        )}
                        style={{ width: `${Math.min(100, Math.abs(100 - impact.deviation))}%` }}
                      />
                    </div>
                  </div>

                  {impact.recommendation && (
                    <div className="text-xs text-surface-400 bg-surface-800/50 p-2 rounded flex items-start gap-2">
                      <Info size={12} className="mt-0.5 flex-shrink-0 text-primary-400" />
                      {impact.recommendation}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Historical Trends Tab */}
        {activeTab === 'trends' && (
          <div className="space-y-4">
            {historicalData.length > 0 ? (
              <div className="bg-surface-900/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-surface-300 mb-3">Gas Properties Over Time</h4>
                <Plot
                  data={trendChartData}
                  layout={{
                    ...plotLayout,
                    height: 300,
                    xaxis: {
                      title: { text: 'Time' },
                      gridcolor: '#334155',
                      linecolor: '#475569'
                    },
                    yaxis: {
                      title: { text: 'Molecular Weight' },
                      gridcolor: '#334155',
                      linecolor: '#475569',
                      side: 'left'
                    },
                    yaxis2: {
                      title: { text: 'Heating Value (BTU/scf)' },
                      overlaying: 'y',
                      side: 'right',
                      gridcolor: '#334155'
                    },
                    legend: {
                      orientation: 'h',
                      y: -0.2
                    }
                  }}
                  config={plotConfig}
                  className="w-full"
                />
              </div>
            ) : (
              <div className="bg-surface-900/50 rounded-lg p-8 text-center">
                <TrendingUp size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Historical trend data not available</p>
                <p className="text-xs text-surface-500 mt-1">Data will appear once sufficient readings are collected</p>
              </div>
            )}
          </div>
        )}

        {/* AI Analysis Tab */}
        {activeTab === 'analysis' && (
          <div className="space-y-4">
            {aiAnalysis ? (
              <div className="bg-gradient-to-br from-primary-500/10 to-cyan-500/10 rounded-lg p-4 border border-primary-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <Activity size={16} className="text-primary-400" />
                  <span className="text-sm font-medium text-primary-300">AI-Powered Analysis</span>
                </div>
                <div className="text-sm text-surface-200 leading-relaxed whitespace-pre-line">
                  {aiAnalysis}
                </div>
              </div>
            ) : (
              <div className="bg-surface-900/50 rounded-lg p-4">
                <p className="text-surface-400 text-center">AI analysis loading...</p>
              </div>
            )}

            {/* Quick Recommendations */}
            <div className="bg-surface-900/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-surface-300 mb-3 flex items-center gap-2">
                <Target size={14} />
                Optimization Recommendations
              </h4>
              <div className="space-y-2">
                {compositionStats.outOfRangeCount > 0 && (
                  <div className="flex items-start gap-2 p-2 bg-status-warning/10 rounded border border-status-warning/20">
                    <AlertTriangle size={14} className="text-status-warning mt-0.5" />
                    <div className="text-sm text-surface-200">
                      {compositionStats.outOfRangeCount} component(s) outside normal range. Review gas source quality.
                    </div>
                  </div>
                )}
                {Math.abs(mwDeviation) > 5 && (
                  <div className="flex items-start gap-2 p-2 bg-status-warning/10 rounded border border-status-warning/20">
                    <Gauge size={14} className="text-status-warning mt-0.5" />
                    <div className="text-sm text-surface-200">
                      Molecular weight {mwDeviation > 0 ? 'above' : 'below'} target by {Math.abs(mwDeviation).toFixed(1)}%.
                      Adjust VCP settings accordingly.
                    </div>
                  </div>
                )}
                {compositionStats.contaminantTotal > 0.5 && (
                  <div className="flex items-start gap-2 p-2 bg-status-critical/10 rounded border border-status-critical/20">
                    <Droplets size={14} className="text-status-critical mt-0.5" />
                    <div className="text-sm text-surface-200">
                      Elevated contaminant levels detected. Consider upstream filtration enhancement.
                    </div>
                  </div>
                )}
                {compositionStats.healthScore >= 90 && (
                  <div className="flex items-start gap-2 p-2 bg-status-healthy/10 rounded border border-status-healthy/20">
                    <CheckCircle size={14} className="text-status-healthy mt-0.5" />
                    <div className="text-sm text-surface-200">
                      Gas composition within optimal parameters. Continue current monitoring schedule.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
